import {
    ChatBubbleLeftEllipsisIcon,
    ClockIcon,
    DocumentArrowUpIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import {
    ArrowsRightLeftIcon,
    ChatBubbleOvalLeftEllipsisIcon,
    ClipboardDocumentCheckIcon,
    ClipboardDocumentIcon,
    ClipboardDocumentListIcon,
    ClipboardIcon,
    DocumentPlusIcon,
    EnvelopeIcon,
    PaperAirplaneIcon,
    PhoneIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";
import { daysAgo, timeOnly } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import {
    ActivityRowBody,
    activityRowIconClasses,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import { Badge } from "components/ui/badge";
import { BlankSlate } from "components/ui/blank-slate";
import { ClampedText } from "components/ui/clamped-text";
import * as React from "react";
import { breakNewlines } from "util/text";

export type SharedHistoryEntry = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["history"][number];

type HistoryIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const historyIcons: Partial<Record<SharedHistoryEntry["activityType"], HistoryIcon>> = {
    NoteCreate: ChatBubbleLeftEllipsisIcon,
    NoteDelete: TrashIcon,
    TaskCreate: ClipboardDocumentIcon,
    TaskUpdate: ClipboardDocumentListIcon,
    TaskDelete: ClipboardIcon,
    TaskComplete: ClipboardDocumentCheckIcon,
    TaskUncomplete: ClipboardDocumentListIcon,
    TaskReassign: ClipboardDocumentListIcon,
    EntityCreate: DocumentPlusIcon,
    EntityFieldChange: ArrowsRightLeftIcon,
    EntityReassign: UserGroupIcon,
    FileUpload: DocumentArrowUpIcon,
    MessageSent: EnvelopeIcon,
    SmsSent: ChatBubbleOvalLeftEllipsisIcon,
    OutgoingCall: PhoneIcon,
    SentToPartner: PaperAirplaneIcon,
};

const importantBadge = <Badge type="highlight" value="Important" />;

const SummaryImportantBadge: React.FC<{ entry: SharedHistoryEntry }> = ({ entry }) =>
    entry.isImportant ? (
        <span className="ml-2 align-middle">{importantBadge}</span>
    ) : null;

const SharedHistoryItem: React.FC<{ entry: SharedHistoryEntry }> = ({ entry }) => {
    const EntryIcon = historyIcons[entry.activityType] ?? ClockIcon;
    const occurredAt = React.useMemo(
        () => new Date(entry.occurredAt),
        [entry.occurredAt],
    );
    return (
        <li>
            <ActivityRowBody
                leading={
                    <EntryIcon aria-hidden="true" className={activityRowIconClasses} />
                }
                actions={null}>
                <div className="flex justify-between items-start gap-2">
                    <div className="text-sm font-medium text-gray-700">
                        <strong>{entry.actorName}</strong>{" "}
                        <span data-testid="shared-history-summary">{entry.summary}</span>
                        <SummaryImportantBadge entry={entry} />
                    </div>
                    <div className="text-gray-400 text-sm flex-shrink-0">
                        <span className="text-gray-600">{daysAgo(occurredAt, "")}</span>{" "}
                        at <span className="text-gray-600">{timeOnly(occurredAt)}</span>
                    </div>
                </div>
                {entry.detail ? (
                    <div className="mt-1 text-sm text-gray-900">
                        <ClampedText lines={3}>{breakNewlines(entry.detail)}</ClampedText>
                    </div>
                ) : null}
            </ActivityRowBody>
        </li>
    );
};

export const SharedLeadFullHistory: React.FC<{ history: SharedHistoryEntry[] }> = ({
    history,
}) =>
    history.length === 0 ? (
        <BlankSlate>No history yet.</BlankSlate>
    ) : (
        <ActivityRowsList className="" testId="shared-lead-history">
            {history.map(entry => (
                <SharedHistoryItem key={entry.id} entry={entry} />
            ))}
        </ActivityRowsList>
    );
